/**
 * A document read back after it has gone out — usually after it was signed.
 *
 * Jonathon, 2026-09-02: "i need to be able to see every document in the docs
 * section... I need to be able to see the signed doc. best if it saves it as a
 * pdf."
 *
 * The customer's link cannot answer that. It is deliberately one-way: once a
 * proposal is accepted the endpoint refuses it with "This has already been
 * signed", which is the right answer for a customer opening yesterday's email
 * and the wrong one for the office asking what was agreed. Opening it as staff
 * is worse than useless on a live document — the same call stamps a view onto
 * the audit trail that no customer made.
 *
 * So the office reads the frozen snapshot directly and renders the same
 * document, with the choice the customer actually made locked into it. This
 * module is the part of that which is a decision rather than a layout: what a
 * signed document says it is, and what goes on the PDF, in order. The drawing
 * lives in apps/web/src/lib/documentPdf.ts, and nothing but this file decides
 * what is on it.
 *
 * Everything here reads the snapshot and the receipt. Neither is recomputed
 * from the job: a document is frozen at send (ADR 0032) and the acceptance is
 * written once and never edited, so the record has to say what the customer was
 * looking at and what they agreed to, not what the estimate says today.
 */

import { formatMoney } from "./money.js";
import type { Cents } from "./money.js";
import {
  chosenPackages,
  type Chosen,
  documentPackages,
  documentUpgrades,
  includedUpgradeKeys,
  presentedDeposit,
  presentedSchedule,
  presentedTotal,
  type ContractSection,
  type CoverSection,
  type DocumentSection,
  type PresentedDocument,
  type PresentedUpgrade,
  type ScopeSection,
} from "./presentation.js";

/**
 * The signature on a document, as `proposal_acceptances` holds it.
 *
 * The money is `numeric` in the database and arrives as dollars, not cents —
 * it is the receipt written at the moment of signing, and it is what the PDF
 * prints. The snapshot's own figures are cents and are used for everything the
 * customer *chose* between; where the two could ever disagree, the receipt
 * wins, because that is the number they signed under.
 */
export interface DocumentSignature {
  signerName: string;
  signerEmail: string | null;
  signerPhone: string | null;
  /** The date the customer put beside their name. Null on an old one. */
  signedDate: string | null;
  /** Our own clock at the moment the signature landed. Never theirs. */
  acceptedAt: string;
  /** The estimate they bought. The first one, where they bought several. */
  estimateId: string;
  /**
   * Every estimate they bought, where the document let them buy more than one
   * (ADR 0120). Absent on a signature recorded before that, and on every
   * ordinary one, where `estimateId` is the whole answer.
   */
  estimateIds?: readonly string[];
  /** The upgrades that were on their roof, ticked or included. */
  upgradeKeys: readonly string[];
  subtotal: number;
  salesTax: number;
  total: number;
  ip: string | null;
  userAgent: string | null;
  customerNote: string | null;
}

/**
 * Everything a signature bought.
 *
 * `estimateIds` where the document allowed several and `estimateId` alone
 * otherwise, so every reader below asks one question and an old signature
 * answers it the same way it always did.
 */
export function signedEstimateIds(
  signature: DocumentSignature,
): readonly string[] {
  const many = signature.estimateIds ?? [];
  return many.length > 0 ? many : [signature.estimateId];
}

/** Money as it arrives from `numeric` — dollars, not cents. */
function dollars(value: number): string {
  return formatMoney(Math.round(value * 100) as Cents);
}

function sectionOf<T extends DocumentSection["id"]>(
  document: PresentedDocument,
  id: T,
): Extract<DocumentSection, { id: T }> | null {
  return (
    (document.sections.find((section) => section.id === id) as
      Extract<DocumentSection, { id: T }> | undefined) ?? null
  );
}

/** The cover, which is where every name on this document comes from. */
export function documentCover(
  document: PresentedDocument,
): CoverSection | null {
  return sectionOf(document, "cover");
}

/**
 * What to call this document, and therefore what to call the file.
 *
 * The estimate number rather than the proposal's id: it is the number on the
 * cover, the number the customer quotes on the phone, and the number the job
 * is filed under. A document with no cover — which nothing builds, but a
 * malformed row could be — still gets a name rather than an empty string.
 */
export function signedDocumentTitle(
  document: PresentedDocument,
  signed: boolean,
): string {
  const cover = documentCover(document);
  const what = signed ? "Signed Agreement" : "Proposal";
  const parts = [what, cover?.estimateNumber, cover?.customerName].filter(
    (part): part is string => Boolean(part),
  );
  return parts.join(" - ");
}

/** The same, as a filename somebody can find in a Downloads folder. */
export function signedDocumentFilename(
  document: PresentedDocument,
  signed: boolean,
): string {
  return `${signedDocumentTitle(document, signed).replace(/[\\/:*?"<>|]/g, "")}.pdf`;
}

/**
 * Every upgrade that ended up on the roof, and what each one cost.
 *
 * Priced from the snapshot, never from the request that signed it — the same
 * rule the Edge Function enforces at signing time, applied again here so the
 * record cannot drift from it. An upgrade the chosen package already covers
 * reads "Included" and adds nothing, however it came to be ticked.
 */
export function recordUpgrades(
  document: PresentedDocument,
  chosen: Chosen,
  keys: readonly string[],
): { upgrade: PresentedUpgrade; included: boolean }[] {
  const wanted = new Set(keys);
  const included = new Set(includedUpgradeKeys(document, chosen));
  return documentUpgrades(document)
    .filter((upgrade) => wanted.has(upgrade.key) || included.has(upgrade.key))
    .map((upgrade) => ({ upgrade, included: included.has(upgrade.key) }));
}

/**
 * A line of the PDF.
 *
 * Deliberately small: a page of a contract is headings, paragraphs, bullets and
 * a handful of label-and-value rows, and anything richer would be a layout
 * decision leaking into the part of this that is business rules. The renderer
 * decides what each of these looks like; this file decides which ones there
 * are and what they say.
 */
export type DocumentBlock =
  | { kind: "title"; text: string }
  | { kind: "heading"; text: string }
  | { kind: "subheading"; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "bullet"; text: string }
  | { kind: "pair"; label: string; value: string }
  | { kind: "total"; label: string; value: string }
  | { kind: "rule" };

/** The head of the document: who it is for, from whom, and about what. */
function coverBlocks(
  document: PresentedDocument,
  signature: DocumentSignature | null,
): DocumentBlock[] {
  const cover = documentCover(document);
  const blocks: DocumentBlock[] = [
    { kind: "title", text: signedDocumentTitle(document, Boolean(signature)) },
  ];
  if (!cover) return blocks;

  blocks.push(
    { kind: "pair", label: "Customer", value: cover.customerName },
    { kind: "pair", label: "Property", value: cover.propertyAddress },
  );
  if (cover.customerEmail)
    blocks.push({ kind: "pair", label: "Email", value: cover.customerEmail });
  if (cover.customerPhone)
    blocks.push({ kind: "pair", label: "Phone", value: cover.customerPhone });
  blocks.push({
    kind: "pair",
    label: "Estimate",
    value: cover.estimateNumber,
  });
  blocks.push({ kind: "pair", label: "Company", value: cover.companyName });
  if (cover.repName)
    blocks.push({ kind: "pair", label: "Sold by", value: cover.repName });
  blocks.push({
    kind: "pair",
    label: "Dated",
    value: `${document.date} · revision ${document.revision}`,
  });
  return blocks;
}

/**
 * The roof they bought — or, on a document nobody has signed, every roof they
 * were offered.
 *
 * The whole point of the record is that it says which one, so an unsigned
 * document must not be allowed to look as though it settled the question. It
 * lists the options and says in words that none of them was chosen.
 */
function packageBlocks(
  document: PresentedDocument,
  signature: DocumentSignature | null,
): DocumentBlock[] {
  const packages = documentPackages(document);
  if (packages.length === 0) return [];

  // Only a signature picks. A document quoting one price is still a document
  // nobody has answered, and heading it "What they bought" would put a purchase
  // on a page whose own signature block says there was none. `basePrice` treats
  // a lone package as settled because a price with no choice in it is still the
  // price; a record is answering a different question.
  // Every one of them, on a document that let them buy more than one: a record
  // naming the roof and quietly leaving out the gutters beside it would be a
  // record of a different contract (ADR 0120).
  const picked = signature
    ? chosenPackages(document, signedEstimateIds(signature))
    : [];

  const blocks: DocumentBlock[] = [
    {
      kind: "heading",
      text: picked.length > 0 ? "What they bought" : "What was offered",
    },
  ];

  if (picked.length === 0) {
    blocks.push({
      kind: "paragraph",
      text: "Nothing on this document has been signed, so no option was chosen.",
    });
  }

  for (const option of picked.length > 0 ? picked : packages) {
    blocks.push({
      kind: "subheading",
      text: `${option.name} — ${option.totalFormatted}`,
    });
    if (option.headline)
      blocks.push({ kind: "paragraph", text: option.headline });
    for (const block of option.blocks) {
      blocks.push({ kind: "paragraph", text: block.heading });
      for (const line of block.lines) {
        // A line with no name is a paragraph, not a named thing — the "Please
        // Note" clauses are the whole of their own line (ADR 0100). Joining an
        // empty name to the body would open the sentence with an em dash.
        const name = line.name.trim();
        const text = name
          ? line.body
            ? `${name} — ${line.body}`
            : name
          : (line.body ?? "");
        if (text) blocks.push({ kind: "bullet", text });
      }
    }
  }
  return blocks;
}

/** The upgrades on the roof, priced the way the document priced them. */
function upgradeBlocks(
  document: PresentedDocument,
  signature: DocumentSignature | null,
): DocumentBlock[] {
  if (!signature) return [];
  const chosen = recordUpgrades(
    document,
    signedEstimateIds(signature),
    signature.upgradeKeys,
  );
  if (chosen.length === 0) return [];

  return [
    { kind: "heading", text: "Upgrades on this roof" },
    ...chosen.map(({ upgrade, included }): DocumentBlock => ({
      kind: "pair",
      label: upgrade.name,
      value: included ? "Included" : upgrade.priceFormatted,
    })),
  ];
}

/**
 * The money.
 *
 * From the receipt when there is one — those three numbers are what the
 * customer signed under and they are immune to every later edit. Without one,
 * from the snapshot, which is the only number that has ever existed for a
 * document nobody has answered.
 */
function priceBlocks(
  document: PresentedDocument,
  signature: DocumentSignature | null,
): DocumentBlock[] {
  if (signature) {
    const blocks: DocumentBlock[] = [{ kind: "heading", text: "The price" }];
    if (signature.salesTax > 0) {
      blocks.push(
        { kind: "pair", label: "Subtotal", value: dollars(signature.subtotal) },
        {
          kind: "pair",
          label: "Sales tax",
          value: dollars(signature.salesTax),
        },
      );
    }
    blocks.push({
      kind: "total",
      label: "Contract total",
      value: dollars(signature.total),
    });
    const deposit = presentedDeposit(
      document,
      signedEstimateIds(signature),
      signature.upgradeKeys,
    );
    if (deposit?.required)
      blocks.push({
        kind: "pair",
        label: "Deposit",
        value: deposit.amountFormatted,
      });
    blocks.push(...scheduleBlocks(document, signature));
    return blocks;
  }

  const total = presentedTotal(document);
  if (!total) return [];
  return [
    { kind: "heading", text: "The price" },
    { kind: "total", label: "Total", value: total.totalFormatted },
  ];
}

/**
 * The rep's payment schedule, where the proposal printed one (ADR 0267).
 *
 * Jonathon, 2026-10-06, asked whether the signed copy should list the whole
 * schedule: "The signed copy lists the whole schedule, in the rep's words, as
 * the proposal does." So it is the proposal's own `presentedSchedule`, read
 * from the frozen snapshot with what the signature bought and the upgrades on
 * it, the same inputs the customer's page had at the moment they signed. The
 * lines, the wording and the amounts are therefore the ones they signed under,
 * the Final is the remainder, and an estimate left on the standard split adds
 * nothing here, as it adds nothing to the proposal.
 */
function scheduleBlocks(
  document: PresentedDocument,
  signature: DocumentSignature,
): DocumentBlock[] {
  const rows = presentedSchedule(
    document,
    signedEstimateIds(signature),
    signature.upgradeKeys,
  );
  if (!rows) return [];
  return [
    { kind: "subheading", text: "Payment schedule" },
    ...rows.map((row): DocumentBlock => ({
      kind: "pair",
      label: row.triggerText ? `${row.label} — ${row.triggerText}` : row.label,
      value: row.amountFormatted,
    })),
  ];
}

/** The eight steps, where the document carries them. */
function scopeBlocks(document: PresentedDocument): DocumentBlock[] {
  const scope = sectionOf(document, "scope") as ScopeSection | null;
  if (!scope) return [];
  return [
    { kind: "heading", text: scope.heading },
    ...scope.steps.flatMap((step): DocumentBlock[] => [
      { kind: "subheading", text: step.heading },
      ...step.points.map((point): DocumentBlock => ({
        kind: "bullet",
        text: point,
      })),
    ]),
    { kind: "paragraph", text: `${scope.closer.bar} ${scope.closer.line}` },
  ];
}

/**
 * The agreement itself, word for word as it was frozen.
 *
 * Every block, including the initial boxes a document from before 2026-08-16
 * still draws — with what the customer actually initialled beside each one,
 * because a box that was left empty on a signed contract is the single most
 * useful thing this page can tell somebody.
 */
function contractBlocks(
  document: PresentedDocument,
  signature: DocumentSignature | null,
  initials: Record<string, unknown>,
): DocumentBlock[] {
  const contract = sectionOf(document, "contract") as ContractSection | null;
  if (!contract) return [];

  return [
    { kind: "heading", text: contract.heading },
    ...contract.blocks.map((block): DocumentBlock => {
      if (block.kind === "heading")
        return { kind: "subheading", text: block.text };
      if (block.kind === "initial")
        return {
          kind: "pair",
          label: block.text,
          value: signature
            ? initials[block.key]
              ? "Initialled"
              : "Not initialled"
            : "—",
        };
      return { kind: "paragraph", text: block.text };
    }),
  ];
}

/**
 * Who signed, when, and from where.
 *
 * The company's half comes off the snapshot rather than off today's settings,
 * for the reason the customer's page does the same: the name on the
 * certificate has to be the name that was on the paper they read.
 */
function signatureBlocks(
  document: PresentedDocument,
  signature: DocumentSignature | null,
  companySignedAt: string | null,
): DocumentBlock[] {
  const contract = sectionOf(document, "contract") as ContractSection | null;
  const companySigner = contract?.companySigner ?? null;
  const companyDate =
    contract?.companySignedDate ?? companySignedAt?.slice(0, 10) ?? null;

  if (!signature) {
    return [
      { kind: "rule" },
      { kind: "heading", text: "Signature" },
      {
        kind: "paragraph",
        text: "This document has not been signed.",
      },
      ...(companySigner
        ? [
            {
              kind: "pair" as const,
              label: "Signed for the company by",
              value: `${companySigner}${companyDate ? ` on ${companyDate}` : ""}`,
            },
          ]
        : []),
    ];
  }

  const blocks: DocumentBlock[] = [
    { kind: "rule" },
    { kind: "heading", text: "Signature" },
  ];
  if (companySigner)
    blocks.push({
      kind: "pair",
      label: "For the company",
      value: `${companySigner}${companyDate ? ` — ${companyDate}` : ""}`,
    });
  blocks.push({
    kind: "pair",
    label: "Customer",
    value: `${signature.signerName}${
      signature.signedDate ? ` — ${signature.signedDate}` : ""
    }`,
  });
  if (signature.signerEmail)
    blocks.push({
      kind: "pair",
      label: "Email",
      value: signature.signerEmail,
    });
  if (signature.signerPhone)
    blocks.push({
      kind: "pair",
      label: "Phone",
      value: signature.signerPhone,
    });
  blocks.push({
    kind: "pair",
    label: "Signed electronically",
    value: signature.acceptedAt,
  });
  if (signature.ip)
    blocks.push({ kind: "pair", label: "From", value: signature.ip });
  blocks.push({
    kind: "pair",
    label: "Document",
    value: `Revision ${document.revision} · contract ${document.contractVersion}`,
  });
  if (signature.customerNote)
    blocks.push({
      kind: "paragraph",
      text: `Customer note: ${signature.customerNote}`,
    });
  blocks.push({
    kind: "paragraph",
    text:
      "The customer agreed to do business electronically and typed their name " +
      "as their signature. This record is a copy of the document as it was " +
      "sent; it cannot have changed since.",
  });
  return blocks;
}

/**
 * The whole document, in the order it prints.
 *
 * One function so that the PDF and any test of it are looking at the same
 * decision. Photographs are not on it — the findings gallery and the colour
 * charts are pictures hosted elsewhere, and a record that silently drops them
 * when a bucket is slow is worse than one that never claimed to carry them.
 * The online copy in Roof Board still shows them.
 */
export function documentRecordBlocks({
  document,
  signature,
  companySignedAt = null,
  initials = {},
}: {
  document: PresentedDocument;
  signature: DocumentSignature | null;
  companySignedAt?: string | null;
  initials?: Record<string, unknown>;
}): DocumentBlock[] {
  return [
    ...coverBlocks(document, signature),
    ...packageBlocks(document, signature),
    ...upgradeBlocks(document, signature),
    ...priceBlocks(document, signature),
    ...scopeBlocks(document),
    ...contractBlocks(document, signature, initials),
    ...signatureBlocks(document, signature, companySignedAt),
  ];
}
